import React, { useState } from "react";
import { Card, CardContent, Typography, Divider, Box } from "@mui/material";
import { Order, Payment } from "../../dto/orderSummaryTypes";
import { OrderListItem } from "../../common/orderListItem/OrderListItem";
import { ActionButton } from "../../common/actionButton/ActionButton";
import {
  rightCardStyle,
  rightCardContentStyle,
  sectionTitleStyle,
  dividerStyle,
  notesBoxStyle,
  notesTextStyle,
  orderSummaryRowStyle,
  orderSummaryTotalStyle,
  totalLabelStyle,
  totalValueStyle,
  cancelOrderButtonContainerStyle,
  breakdownRowStyle,
  breakdownTotalStyle,
} from "./OrderTrackingStyles";

type RightProps = {
  orders: Order[];
  payment: Payment;
  notes?: string | null;
  isCancellationAllowed: boolean;
  isOrderCancelled: boolean;
  onCancelRequest: () => void;
};

const truncate = (text: string, max: number) => {
  if (!text) return "";
  if (text.length <= max) return text;
  return text.slice(0, max).trim() + "...";
};

const OrderSummaryRight: React.FC<RightProps> = ({
  orders,
  payment,
  notes,
  isCancellationAllowed,
  isOrderCancelled,
  onCancelRequest,
}) => {
  const [showAllItems, setShowAllItems] = useState(false);
  const visible = showAllItems ? orders : orders.slice(0, 2);

  // Use backend payment data for order summary
  const total = payment.exciseBreakdown
    ? payment.exciseBreakdown.grandTotal + (payment.serviceTotal || 0)
    : (payment.exciseTotal || 0) + (payment.serviceTotal || 0);

  return (
    <Card sx={rightCardStyle}>
      <CardContent sx={rightCardContentStyle}>
        {/* Inside package */}
        <Typography variant="subtitle1" sx={sectionTitleStyle}>
          Inside package
        </Typography>
        <Box>
          {visible.map((o, i) => (
            <OrderListItem
              key={o.id ?? i}
              {...o}
              name={truncate(o.name, 30)}
              updateItem={() => {
                /* No-op for read-only */
              }}
              isEditable={false}
            />
          ))}

          {orders.length > 2 && !showAllItems && !isOrderCancelled && (
            <ActionButton
              outlined
              label={`See ${orders.length - 2} product${orders.length - 2 > 1 ? "s" : ""} more`}
              onClick={() => setShowAllItems(true)}
              sx={{ mt: 1 }}
            />
          )}
          {orders.length > 2 && showAllItems && !isOrderCancelled && (
            <ActionButton
              outlined
              label="See fewer"
              onClick={() => setShowAllItems(false)}
              sx={{ mt: 1 }}
            />
          )}
        </Box>

        <Divider sx={dividerStyle} />

        {/* Notes */}
        {notes && (
          <>
            <Typography variant="subtitle1" sx={sectionTitleStyle}>
              Notes
            </Typography>
            <Box sx={notesBoxStyle}>
              <Typography variant="body2" sx={notesTextStyle}>
                {notes}
              </Typography>
            </Box>
            <Divider sx={dividerStyle} />
          </>
        )}

        {/* Detailed Excise Breakdown */}
        {payment.exciseBreakdown && (
          <>
            <Divider sx={dividerStyle} />
            <Typography variant="subtitle1" sx={sectionTitleStyle}>
              Excise Invoice Breakdown
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Box sx={breakdownRowStyle}>
                <Typography variant="body2">Order Amount</Typography>
                <Typography variant="body2">
                  Rs. {payment.exciseBreakdown.orderAmount.toFixed(2)}
                </Typography>
              </Box>
              <Box sx={breakdownRowStyle}>
                <Typography variant="body2">
                  Excise Duty (Rs.{" "}
                  {payment.exciseBreakdown.exciseDutyRatePerLitre}/L)
                </Typography>
                <Typography variant="body2">
                  Rs. {payment.exciseBreakdown.exciseDuty.toFixed(2)}
                </Typography>
              </Box>
              <Box sx={breakdownRowStyle}>
                <Typography variant="body2">
                  Additional Excise Duty (
                  {payment.exciseBreakdown.additionalExciseDutyPercentage}%)
                </Typography>
                <Typography variant="body2">
                  Rs. {payment.exciseBreakdown.additionalExciseDuty.toFixed(2)}
                </Typography>
              </Box>
              <Box sx={breakdownRowStyle}>
                <Typography variant="body2">Sub Total</Typography>
                <Typography variant="body2">
                  Rs. {payment.exciseBreakdown.subTotal.toFixed(2)}
                </Typography>
              </Box>
              <Box sx={breakdownRowStyle}>
                <Typography variant="body2">
                  TCS ({payment.exciseBreakdown.tcsPercentage}%)
                </Typography>
                <Typography variant="body2">
                  Rs. {payment.exciseBreakdown.tcs.toFixed(2)}
                </Typography>
              </Box>
              <Box sx={breakdownTotalStyle}>
                <Typography variant="body2">Excise Total</Typography>
                <Typography variant="body2">
                  Rs. {payment.exciseBreakdown.grandTotal.toFixed(2)}
                </Typography>
              </Box>
            </Box>
            <Divider sx={dividerStyle} />
            <Box sx={orderSummaryTotalStyle}>
              <Typography sx={totalLabelStyle}>
                Service Invoice Total
              </Typography>
              <Typography sx={totalValueStyle}>
                Rs. {(payment.serviceTotal ?? 0).toFixed(2)}
              </Typography>
            </Box>

            <Box sx={orderSummaryTotalStyle}>
              <Typography sx={totalLabelStyle}>Grand Total</Typography>
              <Typography sx={totalValueStyle}>
                Rs. {total.toFixed(2)}
              </Typography>
            </Box>
          </>
        )}

        {/* Cancel Order */}
        {isCancellationAllowed && (
          <Box sx={cancelOrderButtonContainerStyle}>
            <ActionButton
              label="Cancel Order"
              onClick={onCancelRequest}
              disabled={isOrderCancelled}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default OrderSummaryRight;
